// App.js — Auth-gated shell with role-aware nav and OTP session support
import React, { useState, useEffect, createContext, useContext } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';   // npm install jwt-decode

import LoginPage        from './pages/LoginPage';
import Overview         from './pages/Overview';
import Customers        from './pages/Customers';
import CustomerOverview from './pages/CustomerOverview';
import UsageDetail      from './pages/UsageDetail';
import EtlRuns          from './pages/EtlRuns';
import DbSizes          from './pages/DbSizes';
import Volumes          from './pages/Volumes';
import Sessions         from './pages/Sessions';
import CostPage         from './pages/CostPage';
import FullCostPage     from './pages/FullCostPage';
import Bundles          from './pages/Bundles';
import Burndown         from './pages/Burndown';
import UserManagement   from './pages/UserManagement';
import AboutDialog      from './components/AboutDialog';
import { VERSION } from './version';
import './App.css';

// ── Auth context ──────────────────────────────────────────────────────────────
export const AuthContext = createContext({ role: 'readonly', email: '', auth: '' });
export const useAuth = () => useContext(AuthContext);

// ── Nav definition ────────────────────────────────────────────────────────────
// minRole: minimum role required to see the item
// 'readonly' = everyone, 'editor' = editor+admin, 'admin' = admin only
const NAV = [
  { id: 'overview',          label: 'Overview',        minRole: 'readonly' },
  { id: 'customers',         label: 'Customers',       minRole: 'readonly' },
  { id: 'bundles',           label: 'Bundles',         minRole: 'readonly' },
  { id: 'burndown',          label: 'Usage burn-down', minRole: 'readonly' },
  { id: 'customer_overview', label: 'Customer profile',minRole: 'readonly' },
  { id: 'usage',             label: 'Usage detail',    minRole: 'readonly' },
  { id: 'sessions',          label: 'Sessions',        minRole: 'readonly' },
  { id: 'dbsizes',           label: 'DB sizes',        minRole: 'readonly' },
  { id: 'volumes',           label: 'Volumes',         minRole: 'readonly' },
  { id: 'cost',              label: 'Cost',            minRole: 'readonly' },
  { id: 'cost_full',         label: 'Full cost',       minRole: 'readonly' },
  { id: 'etl',               label: 'ETL runs',        minRole: 'readonly' },
  { id: 'users',             label: 'User management', minRole: 'admin'    },
];

const ROLE_ORDER = { readonly: 0, editor: 1, admin: 2 };
const hasRole = (userRole, minRole) => (ROLE_ORDER[userRole] ?? 0) >= (ROLE_ORDER[minRole] ?? 0);

// ── Helpers ───────────────────────────────────────────────────────────────────

function getOtpSession() {
  const token = localStorage.getItem('otp_token');
  if (!token) return null;
  try {
    const payload = jwtDecode(token);
    if (payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('otp_token');
      return null;
    }
    return { email: payload.email, role: payload.role || 'readonly', auth: 'otp' };
  } catch {
    localStorage.removeItem('otp_token');
    return null;
  }
}

function getRoleInitials(name) {
  if (!name) return '?';
  const parts = name.split(/[\s@]+/);
  return (parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '');
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {
  const { accounts } = useMsal();
  const entraAuth    = useIsAuthenticated();

  const [otpSession,   setOtpSession]   = useState(() => getOtpSession());
  const [page,         setPage]         = useState('overview');
  const [dimension,    setDimension]    = useState('last_year');
  const [activeOnly,   setActiveOnly]   = useState(true);
  const [showAbout,    setShowAbout]    = useState(false);

  // Determine active auth context
  let authCtx = { role: 'readonly', email: '', auth: '' };

  if (otpSession) {
    authCtx = otpSession;
  } else if (entraAuth && accounts.length > 0) {
    const acc = accounts[0];
    // Role for Entra users is resolved server-side; we default to 'editor' here
    // and the /me endpoint can be called if client-side role gating is needed.
    // For nav purposes we store the role in a state var — see useEffect below.
    authCtx = {
      email: acc.username || acc.idTokenClaims?.preferred_username || '',
      role:  'editor',   // updated below via /me
      auth:  'entra',
    };
  }

  const [resolvedRole, setResolvedRole] = useState(authCtx.role);

  // Fetch actual role from /me after auth is established
  useEffect(() => {
    if (!entraAuth && !otpSession) return;
    import('./api').then(({ default: api }) => {
      api.get('/api/v1/users/me')
        .then(res => setResolvedRole(res.data.role || 'editor'))
        .catch(() => {});
    });
  }, [entraAuth, otpSession]);

  authCtx = { ...authCtx, role: resolvedRole };

  // ── Not authenticated → show login ────────────────────────────────────────
  if (!entraAuth && !otpSession) {
    return (
      <LoginPage
        onOtpToken={(token) => {
          const session = getOtpSession();
          setOtpSession(session);
        }}
      />
    );
  }

  // ── Guard current page against role ──────────────────────────────────────
  const currentNav = NAV.find(n => n.id === page);
  if (currentNav && !hasRole(resolvedRole, currentNav.minRole)) {
    setPage('overview');
  }

  const visibleNav = NAV.filter(n => hasRole(resolvedRole, n.minRole));

  // ── Sign out ──────────────────────────────────────────────────────────────
  const signOut = () => {
    if (otpSession) {
      localStorage.removeItem('otp_token');
      setOtpSession(null);
    } else {
      import('./msalInstance').then(({ msalInstance }) => {
        msalInstance.logoutRedirect();
      });
    }
  };

  // ── Page components ───────────────────────────────────────────────────────
  const pages = {
    overview:          <Overview         dimension={dimension} activeOnly={activeOnly} />,
    customers:         <Customers        dimension={dimension} activeOnly={activeOnly} />,
    bundles:           <Bundles />,
    burndown:          <Burndown />,
    customer_overview: <CustomerOverview dimension={dimension} activeOnly={activeOnly} />,
    usage:             <UsageDetail      dimension={dimension} activeOnly={activeOnly} />,
    sessions:          <Sessions         dimension={dimension} activeOnly={activeOnly} />,
    dbsizes:           <DbSizes          dimension={dimension} activeOnly={activeOnly} />,
    volumes:           <Volumes          dimension={dimension} activeOnly={activeOnly} />,
    cost:              <CostPage         dimension={dimension} activeOnly={activeOnly} />,
    cost_full:         <FullCostPage     dimension={dimension} activeOnly={activeOnly} />,
    etl:               <EtlRuns />,
    users:             <UserManagement />,
  };

  const displayName = authCtx.auth === 'otp'
    ? authCtx.email.split('@')[0]
    : (accounts[0]?.name || authCtx.email.split('@')[0] || 'User');

  const roleBadge = {
    admin:    { background: '#fef3c7', color: '#92400e' },
    editor:   { background: '#dbeafe', color: '#1e40af' },
    readonly: { background: '#f3f4f6', color: '#6b7280' },
  }[resolvedRole] || {};

  return (
    <AuthContext.Provider value={authCtx}>
      <div className="shell">
        <aside className="sidebar">
          <div className="sidebar-logo">
            <div className="logo-mark">StarLIMS Cloud</div>
            <div className="logo-sub">Usage reporting</div>
          </div>

          <nav className="nav">
            {visibleNav.map(n => (
              <button
                key={n.id}
                className={`nav-item ${page === n.id ? 'active' : ''}`}
                onClick={() => setPage(n.id)}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="user-initials">{getRoleInitials(displayName).toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 6px',
                  borderRadius: 10, ...roleBadge,
                }}>
                  {resolvedRole}
                </span>
                {authCtx.auth === 'otp' && (
                  <span style={{ fontSize: 10, color: '#aaa' }}>guest</span>
                )}
              </div>
            </div>
            <button
              onClick={signOut}
              title="Sign out"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#aaa', fontSize: 16, padding: 4, flexShrink: 0,
              }}
            >
              ⏻
            </button>
          </div>

          <div
            onClick={() => setShowAbout(true)}
            style={{
              padding: '12px 16px',
              borderTop: '1px solid #e5e5e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              fontSize: 11,
              color: '#999',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            title="View changelog"
          >
            <span>Version {VERSION}</span>
            <span style={{ fontSize: 14 }}>ℹ</span>
          </div>
        </aside>

        <div className="main">
          <header className="topbar">
            <div className="topbar-title">
              {visibleNav.find(n => n.id === page)?.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#666', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={e => setActiveOnly(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Active only
              </label>
              <select
                className="dim-select"
                value={dimension}
                onChange={e => setDimension(e.target.value)}
              >
                <option value="last_month">Last month</option>
                <option value="last_year">Last 12 months</option>
                <option value="all_time">All time</option>
              </select>
            </div>
          </header>

          <div className="page-content">
            {pages[page]}
          </div>
        </div>

        <AboutDialog isOpen={showAbout} onClose={() => setShowAbout(false)} />
      </div>
    </AuthContext.Provider>
  );
}
